import { c as _c } from "react/compiler-runtime";
import { cn } from '@/lib/utils';
import { Check, ChevronDown } from '@/components/ui/icons/Icons';
import * as SelectPrimitive from '@radix-ui/react-select';
import { ComponentPropsWithoutRef, ReactNode, Ref } from 'react';
type SelectProps = ComponentPropsWithoutRef<typeof SelectPrimitive.Root>;
const Select = t0 => {
  const $ = _c(6);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== children || $[4] !== props) {
    t1 = <SelectPrimitive.Root {...props}>{children}</SelectPrimitive.Root>;
    $[3] = children;
    $[4] = props;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
Select.displayName = SelectPrimitive.Root.displayName;
type SelectTriggerProps = ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
  ref?: Ref<HTMLButtonElement>;
};
function SelectTrigger(t0) {
  const $ = _c(13);
  let children;
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = ref;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    ref = $[4];
  }
  let t1;
  if ($[5] !== className) {
    t1 = cn("flex h-9 w-full items-center justify-between gap-2 overflow-hidden rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background data-[placeholder]:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 [&>span:first-child]:min-w-0 [&>span:first-child]:truncate [&>span:first-child]:text-left", className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <SelectPrimitive.Icon asChild={true}><ChevronDown className="size-4 shrink-0 opacity-50" /></SelectPrimitive.Icon>;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== children || $[9] !== props || $[10] !== ref || $[11] !== t1) {
    t3 = <SelectPrimitive.Trigger ref={ref} className={t1} {...props}>{children}{t2}</SelectPrimitive.Trigger>;
    $[8] = children;
    $[9] = props;
    $[10] = ref;
    $[11] = t1;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
}
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;
type SelectContentProps = ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & {
  ref?: Ref<HTMLDivElement>;
  footer?: ReactNode;
};
function SelectContent(t0) {
  const $ = _c(22);
  let children;
  let className;
  let footer;
  let props;
  let ref;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      children,
      position: t1,
      ref,
      footer,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = footer;
    $[4] = props;
    $[5] = ref;
    $[6] = t1;
  } else {
    children = $[1];
    className = $[2];
    footer = $[3];
    props = $[4];
    ref = $[5];
    t1 = $[6];
  }
  const position = t1 === undefined ? "popper" : t1;
  const t2 = position === "popper" && "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1";
  let t3;
  if ($[7] !== className || $[8] !== t2) {
    t3 = cn("relative z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2", t2, className);
    $[7] = className;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const t4 = position === "popper" && "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]";
  let t5;
  if ($[10] !== t4) {
    t5 = cn("p-1 max-h-60 overflow-y-auto", t4);
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== children || $[13] !== t5) {
    t6 = <SelectPrimitive.Viewport className={t5}>{children}</SelectPrimitive.Viewport>;
    $[12] = children;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== footer || $[16] !== position || $[17] !== props || $[18] !== ref || $[19] !== t3 || $[20] !== t6) {
    t7 = <SelectPrimitive.Portal><SelectPrimitive.Content ref={ref} className={t3} position={position} {...props}>{t6}{footer}</SelectPrimitive.Content></SelectPrimitive.Portal>;
    $[15] = footer;
    $[16] = position;
    $[17] = props;
    $[18] = ref;
    $[19] = t3;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
}
SelectContent.displayName = SelectPrimitive.Content.displayName;
type SelectItemProps = ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
  ref?: Ref<HTMLDivElement>;
};
function SelectItem(t0) {
  const $ = _c(15);
  let children;
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = ref;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    ref = $[4];
  }
  let t1;
  if ($[5] !== className) {
    t1 = cn("relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-[#F3F4F6] data-[disabled]:pointer-events-none data-[disabled]:opacity-50", className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center"><SelectPrimitive.ItemIndicator><Check className="size-4" /></SelectPrimitive.ItemIndicator></span>;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== children) {
    t3 = <SelectPrimitive.ItemText className="truncate">{children}</SelectPrimitive.ItemText>;
    $[8] = children;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== props || $[11] !== ref || $[12] !== t1 || $[13] !== t3) {
    t4 = <SelectPrimitive.Item ref={ref} className={t1} {...props}>{t2}{t3}</SelectPrimitive.Item>;
    $[10] = props;
    $[11] = ref;
    $[12] = t1;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}
SelectItem.displayName = SelectPrimitive.Item.displayName;
const SelectValue = SelectPrimitive.Value;
const SelectGroup = SelectPrimitive.Group;
function SelectLabel(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("px-2 py-1.5 text-xs font-semibold text-muted-foreground", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <SelectPrimitive.Label className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}

// Compose the Select component with its subcomponents
const SelectComponent = Object.assign(Select, {
  Trigger: SelectTrigger,
  Content: SelectContent,
  Item: SelectItem,
  Value: SelectValue,
  Group: SelectGroup,
  Label: SelectLabel
});
export { SelectComponent as Select };